import React, { useState } from 'react'
import moment from 'moment'
import * as yup from 'yup'

import * as UserAPI from '../../login/UserAPI'
import * as ClientAPI from '../../client/ClientAPI'
import * as PromiseUtils from '../../commons/PromiseUtils'
import * as DetailedOrderReportAPI from '../DetailedOrderReportAPI'

import { dateFormatToReport } from '../../commons/DateUtils'
import { MessageType, addMessage } from '../../commons/MessageUtils'
import { exportDetailedOrderReportToCsv } from '../DetailedOrderReportCsv'

import DetailedOrderReportView from './DetailedOrderReportView'

export const REQUIRED_DATES_MESSAGE =
    'Informe a data inicial e a data final para consultar o relatório.'
export const INVALID_PERIOD_MESSAGE =
    'A data inicial deve ser anterior ou igual a final.'
export const PERIOD_TOO_LONG_MESSAGE =
    'O período máximo permitido para consulta é de 12 meses. Ajuste as datas e tente novamente.'
export const SEARCH_ERROR_MESSAGE = 'Erro ao consultar o relatório'
export const EXPORT_ERROR_MESSAGE = 'Erro ao exportar o relatório'
export const EXPORT_EMPTY_MESSAGE =
    'Não há pedidos para exportar com os filtros informados.'

export const ROWS_PER_PAGE_OPTIONS = [50, 100, 200]
export const DEFAULT_PAGE_SIZE = ROWS_PER_PAGE_OPTIONS[0]

export const EMPTY_TOTALS = {
    ordersCount: 0,
    totalValue: 0,
    totalWeightInTonnes: 0,
    urgentOrdersCount: 0,
}

/**
 * Mesmo limite do backend (`MAX_REPORT_RANGE_DAYS` em
 * `GetDetailedOrderReport`): até 366 dias contando a data inicial e a final.
 * Diferença de até 365 dias entre elas, ou seja, de 01/11/2024 a 01/11/2025.
 */
export const MAX_PERIOD_DAYS_DIFF = 365

const EMPTY_PERIOD = { initialDate: null, finalDate: null }

const isFilledDate = value =>
    yup.date().nullable().required().isValidSync(value)

export const validatePeriod = ({ initialDate, finalDate }) => {
    if (!isFilledDate(initialDate) || !isFilledDate(finalDate))
        return REQUIRED_DATES_MESSAGE

    if (new Date(initialDate) > new Date(finalDate))
        return INVALID_PERIOD_MESSAGE

    const daysDiff = moment(finalDate)
        .startOf('day')
        .diff(moment(initialDate).startOf('day'), 'days')

    if (daysDiff > MAX_PERIOD_DAYS_DIFF) return PERIOD_TOO_LONG_MESSAGE

    return ''
}

/**
 * Validação enquanto o usuário edita as datas (digitando ou pelo calendário).
 * A falta de uma das datas só é apontada ao consultar/exportar: avisar no meio
 * do preenchimento seria ruído.
 */
const validatePeriodOnChange = period => {
    const message = validatePeriod(period)

    return message === REQUIRED_DATES_MESSAGE ? '' : message
}

export default function DetailedOrderReportContainer() {
    const [period, setPeriod] = useState(EMPTY_PERIOD)
    const [selectedSellers, setSelectedSellers] = useState([])
    const [selectedClients, setSelectedClients] = useState([])
    const [urgency, setUrgency] = useState('ALL')

    const [periodError, setPeriodError] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [isExporting, setIsExporting] = useState(false)
    const [hasSearched, setHasSearched] = useState(false)

    const [orders, setOrders] = useState([])
    const [totals, setTotals] = useState(EMPTY_TOTALS)
    const [totalRecords, setTotalRecords] = useState(0)
    const [first, setFirst] = useState(0)
    const [rows, setRows] = useState(DEFAULT_PAGE_SIZE)

    const [expandedRows, setExpandedRows] = useState(null)

    // Vendedor é identificado por `_id`; cliente, por `id`.
    const mountFilters = () => ({
        ...period,
        sellers: selectedSellers.map(({ value }) => value._id),
        clients: selectedClients.map(({ value }) => value.id),
        urgency,
    })

    const fetchReport = async (page, size) => {
        setIsLoading(true)
        setExpandedRows(null)

        await PromiseUtils.PromiseWrapper({
            promise: DetailedOrderReportAPI.searchDetailedOrderReport(
                page,
                size,
                mountFilters()
            ),
            onFulfilled: ({ data }) => {
                setOrders(data.orders)
                setTotalRecords(data.total || 0)
                setTotals(data.totals || EMPTY_TOTALS)
                setHasSearched(true)
            },
            // Falha não é resultado vazio: sem `hasSearched`, a tela não diz
            // "Nenhum pedido encontrado" para uma consulta que nem rodou.
            onRejected: err => {
                setOrders([])
                setTotalRecords(0)
                setTotals(EMPTY_TOTALS)
                setHasSearched(false)
                addMessage(
                    MessageType.ERROR,
                    err?.message || SEARCH_ERROR_MESSAGE
                )
            },
            callback: () => setIsLoading(false),
        })
    }

    const handleSearch = () => {
        const message = validatePeriod(period)
        setPeriodError(message)

        if (message) return

        setFirst(0)
        fetchReport(1, rows)
    }

    const handlePagination = event => {
        if (validatePeriod(period)) return

        setFirst(event.first)
        setRows(event.rows)
        fetchReport(event.page + 1, event.rows)
    }

    /**
     * A exportação busca o conjunto filtrado inteiro (`export=true`), nunca o
     * estado da página visível.
     */
    const handleExport = async () => {
        const message = validatePeriod(period)
        setPeriodError(message)

        if (message) return

        setIsExporting(true)

        try {
            const { data } =
                await DetailedOrderReportAPI.fetchDetailedOrderReportForExport(
                    mountFilters()
                )

            if (!data.orders.length) {
                addMessage(MessageType.ERROR, EXPORT_EMPTY_MESSAGE)
                return
            }

            exportDetailedOrderReportToCsv(data.orders, {
                dateFrom: dateFormatToReport(period.initialDate),
                dateTo: dateFormatToReport(period.finalDate),
            })
        } catch (err) {
            console.error(err)
            addMessage(MessageType.ERROR, err?.message || EXPORT_ERROR_MESSAGE)
        } finally {
            setIsExporting(false)
        }
    }

    const handleClearFilters = () => {
        setPeriod(EMPTY_PERIOD)
        setSelectedSellers([])
        setSelectedClients([])
        setUrgency('ALL')
        setPeriodError('')
        setOrders([])
        setTotals(EMPTY_TOTALS)
        setTotalRecords(0)
        setFirst(0)
        setRows(DEFAULT_PAGE_SIZE)
        setExpandedRows(null)
        setHasSearched(false)
    }

    const handlePeriodChange = event => {
        const { name, value } = event.target
        const nextPeriod = { ...period, [name]: value }

        setPeriod(nextPeriod)
        setPeriodError(validatePeriodOnChange(nextPeriod))
    }

    const fetchSellers = async () => {
        return PromiseUtils.PromiseWrapper({
            promise: UserAPI.getAllSellersAndStc(),
            onFulfilled: ({ data }) =>
                data.map(seller => ({ label: seller.name, value: seller })),
        })
    }

    const fetchClients = async term => {
        return PromiseUtils.PromiseWrapper({
            promise: ClientAPI.getAllClients(15, 0, term),
            onFulfilled: ({ data }) =>
                data.map(client => ({
                    label: client.owner.name,
                    value: client,
                })),
        })
    }

    return (
        <DetailedOrderReportView
            period={period}
            periodError={periodError}
            urgency={urgency}
            setUrgency={setUrgency}
            selectedSellers={selectedSellers}
            setSelectedSellers={setSelectedSellers}
            selectedClients={selectedClients}
            setSelectedClients={setSelectedClients}
            isLoading={isLoading}
            isExporting={isExporting}
            hasSearched={hasSearched}
            orders={orders}
            totals={totals}
            totalRecords={totalRecords}
            first={first}
            rows={rows}
            expandedRows={expandedRows}
            setExpandedRows={setExpandedRows}
            handlePeriodChange={handlePeriodChange}
            handleSearch={handleSearch}
            handlePagination={handlePagination}
            handleExport={handleExport}
            handleClearFilters={handleClearFilters}
            fetchSellers={fetchSellers}
            fetchClients={fetchClients}
        />
    )
}
